import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MAIN_SITE_URL } from '@/mocks/site';
import { categories } from '@/mocks/categories';
import CategoryDropdown from './CategoryDropdown';

const LANGUAGES = [
  { code: 'en', key: 'en' },
  { code: 'pl', key: 'pl' },
  { code: 'es', key: 'es' },
  { code: 'fr', key: 'fr' },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(true);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const currentLang = LANGUAGES.find((l) => l.code === (i18n.language || 'en')) || LANGUAGES[0];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b-2 border-background-200 bg-background-50">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 md:h-[72px] md:px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" onClick={() => setMobileOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-fill text-lg" />
          </span>
          <span className="font-heading text-base font-extrabold text-foreground-950 md:text-lg">
            {t('common.brand')}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          <Link
            to="/"
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 transition-colors hover:text-primary-700"
          >
            {t('common.nav.home')}
          </Link>
          <div className="px-1">
            <CategoryDropdown variant="nav" label={t('common.nav.categories')} />
          </div>
          <Link
            to="/usfans-spreadsheet-guide"
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 transition-colors hover:text-primary-700"
          >
            {t('common.nav.shoppingGuide')}
          </Link>
          <Link
            to="/#safety"
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 transition-colors hover:text-primary-700"
          >
            {t('common.nav.safety')}
          </Link>
          <Link
            to="/#faq"
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 transition-colors hover:text-primary-700"
          >
            {t('common.nav.faq')}
          </Link>
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* Language selector */}
          <div ref={langRef} className="relative">
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <i className="ri-global-line text-base" />
              <span className="hidden sm:inline">{t(`common.languages.${currentLang.key}`)}</span>
              <i className="ri-arrow-down-s-line text-sm text-foreground-400" />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-lg">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => changeLanguage(l.code)}
                    className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors hover:bg-primary-50 ${
                      l.code === currentLang.code ? 'font-semibold text-primary-700' : 'text-foreground-800'
                    }`}
                  >
                    {t(`common.languages.${l.key}`)}
                    {l.code === currentLang.code && <i className="ri-check-line text-sm" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            {t('common.cta.buyNow')}
          </a>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-900 lg:hidden cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              <i className="ri-home-4-line text-base text-primary-600" />
              {t('common.nav.home')}
            </Link>
            <Link
              to="/usfans-spreadsheet-guide"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              <i className="ri-shopping-bag-3-line text-base text-primary-600" />
              {t('common.nav.shoppingGuide')}
            </Link>

            {/* Mobile categories accordion */}
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setMobileCategoriesOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-grid-line text-base text-primary-600" />
                  {t('common.nav.categories')}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform duration-200 ${
                    mobileCategoriesOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {mobileCategoriesOpen && (
                <div className="mt-2 grid grid-cols-2 gap-2 px-1">
                  {categories.map((c) => (
                    <a
                      key={c.key}
                      href={c.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded bg-background-50">
                        <img
                          src={c.image}
                          alt={t(`categories.${c.key}`)}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="truncate">{t(`categories.${c.key}`)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/#safety"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              <i className="ri-shield-check-line text-base text-primary-600" />
              {t('common.nav.safety')}
            </Link>
            <Link
              to="/#faq"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              <i className="ri-question-line text-base text-primary-600" />
              {t('common.nav.faq')}
            </Link>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-3 py-3 text-sm font-semibold text-background-50"
            >
              <i className="ri-shopping-cart-2-line text-base" />
              {t('common.cta.buyNow')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}